import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'

// Components
import PostCard from '../../Components/Imigo/PostCard'

// Functions
import { fetchPosts, fetchPostsByType } from '../../Functions/apiService'
import { getExcerpt, getImageUrl } from '../../Functions/Utilities'

// Assets
import heroImage from '../../Assets/img/imigo-hero.jpg'
import appScreenshot from '../../Assets/img/imigo-app-screenshot.png'
import appleStore from '../../Assets/img/app-store-badge.png'
import googlePlay from '../../Assets/img/google-play-badge.png'

const HomePage = () => {
  const [articles, setArticles] = useState([])
  const [businesses, setBusinesses] = useState([])
  const [events, setEvents] = useState([])
  const [marketplace, setMarketplace] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchAllPostTypes = async () => {
      try {
        setLoading(true)
        
        // Fetch latest articles
        const articlesData = await fetchPostsByType('Article', { limit: 4 })
        setArticles(articlesData.data || [])
        
        // Fetch businesses
        const businessesData = await fetchPostsByType('Business', { limit: 4 })
        setBusinesses(businessesData.data || [])
        
        // Fetch events
        const eventsData = await fetchPostsByType('Event', { limit: 4 })
        setEvents(eventsData.data || [])
        
        // Fetch marketplace items
        const marketplaceData = await fetchPostsByType('Marketplace', { limit: 4 })
        setMarketplace(marketplaceData.data || [])
        
        setLoading(false)
      } catch (err) {
        console.error('Error fetching posts:', err)
        setError('Failed to load content. Please try again later.')
        setLoading(false)
      }
    }
    
    fetchAllPostTypes()
  }, [])

  // Post type sections configuration
  const postTypeSections = [
    {
      title: 'Latest Articles',
      description: 'Discover our latest articles about migration and living abroad',
      type: 'Article',
      data: articles,
      link: '/articles',
      color: 'bg-gradient-to-r from-purple-600 to-indigo-600'
    },
    {
      title: 'Local Businesses',
      description: 'Find trusted businesses run by your fellow countrymen',
      type: 'Business',
      data: businesses,
      link: '/businesses',
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500'
    },
    {
      title: 'Upcoming Events',
      description: 'Don\'t miss these events happening in your area',
      type: 'Event',
      data: events,
      link: '/events',
      color: 'bg-gradient-to-r from-orange-500 to-amber-500'
    },
    {
      title: 'Marketplace',
      description: 'Buy and sell second-hand items within the community',
      type: 'Marketplace',
      data: marketplace,
      link: '/marketplace',
      color: 'bg-gradient-to-r from-green-500 to-emerald-500'
    }
  ]

  return (
    <div className="imigo-home">
      {/* Hero Section */}
      <section className="hero-section relative py-[100px] bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-darkpurple2/70"></div>
        <Container className="relative z-10">
          <Row className="items-center">
            <Col lg={6} md={12} className="text-white">
              <h1 className="text-5xl font-bold mb-4">New Lands, Familiar Hands</h1>
              <p className="text-xl mb-6">
                Imigo is your smart migration assistant, connecting you with resources, communities, and tools to make your migration journey smoother and more human.
              </p>
              <div className="app-download-buttons flex">
                <a href="https://apps.apple.com/us/app/imigo-connect/id6744267446" target="_blank" rel="noopener noreferrer" className="mr-4">
                  <img src={appleStore} alt="Download on App Store" className="h-[50px]" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=app.imigo" target="_blank" rel="noopener noreferrer">
                  <img src={googlePlay} alt="Get it on Google Play" className="h-[50px]" />
                </a>
              </div>
            </Col>
            <Col lg={6} md={12} className="mt-5 mt-lg-0 text-center">
              <img src={appScreenshot} alt="Imigo App" className="max-w-[300px] mx-auto" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-[80px] bg-lightgray">
        <Container>
          <div className="section-header text-center mb-[50px]">
            <h2 className="text-3xl font-bold mb-3">How Imigo Helps You</h2>
            <p className="text-lg text-gray-600">Your smart companion for every step of your migration journey</p>
          </div>
          
          <Row>
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-comments"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Q&A Community</h3>
                <p>Ask questions, get answers, and share experiences with others who've been through the same journey.</p>
              </div>
            </Col>
            
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-calculator"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Cost Calculator</h3>
                <p>Calculate your living expenses in detail, from accommodation to groceries, in both local currency and your home currency.</p>
              </div>
            </Col>
            
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-store"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Local Businesses</h3>
                <p>Discover restaurants, shops, and services run by your compatriots, complete with locations and contact information.</p>
              </div>
            </Col>
            
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Events & Activities</h3>
                <p>Stay updated on local events and activities organized by both locals and expatriates in your new city.</p>
              </div>
            </Col>
            
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-shopping-cart"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Second-hand Market</h3>
                <p>Buy and sell used items within the community, making settling in and moving out easier and more affordable.</p>
              </div>
            </Col>
            
            <Col lg={4} md={6} className="mb-[30px]">
              <div className="feature-box bg-white p-[30px] rounded-lg shadow-md h-full">
                <div className="feature-icon text-4xl text-neonorange mb-4">
                  <i className="fas fa-users"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Collaboration</h3>
                <p>Connect with others for group purchases, shared services, or business partnerships in your new location.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Post Type Sections */}
      {postTypeSections.map((section, index) => (
        <section key={section.type} className={`post-type-section py-[60px] ${index % 2 === 0 ? 'bg-white' : 'bg-lightgray'}`}>
          <Container>
            <div className={`section-header rounded-lg p-5 mb-[40px] text-white ${section.color}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{section.title}</h2>
                  <p className="mb-0">{section.description}</p>
                </div>
                <Link to={section.link} className="btn btn-light">
                  View All <i className="fas fa-arrow-right ml-2"></i>
                </Link>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : (
              <Row>
                {section.data.length > 0 ? (
                  section.data.map(post => (
                    <Col key={post.id} lg={3} md={6} className="mb-4">
                      <PostCard post={post} postType={section.type} />
                    </Col>
                  ))
                ) : (
                  <Col xs={12}>
                    <div className="text-center py-4">
                      <p>No {section.type.toLowerCase()} found. Check back soon!</p>
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Container>
        </section>
      ))}

      {/* App Download Section */}
      <section className="app-download-section py-[80px] bg-gradient-to-r from-purple-700 to-indigo-800 text-white">
        <Container>
          <Row className="items-center">
            <Col lg={7} md={12} className="mb-5 mb-lg-0">
              <h2 className="text-3xl font-bold mb-4">Get the Full Experience with Our Mobile App</h2>
              <p className="text-lg mb-4">
                Download the Imigo app to access all features including comments, direct messaging, and personalized recommendations.
              </p>
              <ul className="mb-5">
                <li className="mb-3"><i className="fas fa-check-circle mr-2"></i> Full access to Q&A forums with commenting</li>
                <li className="mb-3"><i className="fas fa-check-circle mr-2"></i> Direct messaging with businesses and community members</li>
                <li className="mb-3"><i className="fas fa-check-circle mr-2"></i> Real-time notifications for events and marketplace</li>
                <li className="mb-3"><i className="fas fa-check-circle mr-2"></i> Personalized recommendations based on your interests</li>
              </ul>
              <div className="app-download-buttons flex">
                <a href="https://apps.apple.com/us/app/imigo-connect/id6744267446" target="_blank" rel="noopener noreferrer" className="mr-4">
                  <img src={appleStore} alt="Download on App Store" className="h-[50px]" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=app.imigo" target="_blank" rel="noopener noreferrer">
                  <img src={googlePlay} alt="Get it on Google Play" className="h-[50px]" />
                </a>
              </div>
            </Col>
            <Col lg={5} md={12} className="text-center">
              <img src={appScreenshot} alt="Imigo App" className="max-w-[300px] mx-auto" />
            </Col>
          </Row>
        </Container>
      </section>

    </div>
  )
}

export default HomePage 